import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Allotment } from 'allotment';
import history from 'common/history';
import {
  selectCaseDataRoomSettings,
  selectCurrentCase,
  selectCurrentSelectedFileMetaData,
  selectDocuments,
  selectSpecificParam,
} from 'common/selectors';
import styles from './CaseIntelligencePage.module.scss';
import { ArtefactCitation, PageState } from './types';
import { resolvePageState } from './utils/stateResolver';
import { DashboardTabId } from './utils/dashboardModel';
import { useFetchCaseArtefacts } from '../redux/fetchCaseArtefacts';
import { useFetchCaseTagSuggestions } from '../redux/fetchCaseTagSuggestions';
import { useAcceptCaseTagSuggestion } from '../redux/acceptCaseTagSuggestion';
import { useRejectCaseTagSuggestion } from '../redux/rejectCaseTagSuggestion';
import { useTriggerCrawl } from '../redux/triggerCrawl';
import { useFetchFileDetails } from '../redux/fetchFileDetails';
import { setCurrentSelectedFile } from '../redux/setCurrentSelectedFile';
import { setSidebarOpen } from 'features/common/redux/navbarLayout';
import { setDocSearch } from 'features/viewing/redux/setDocSearch';
import ViewerWrapper from '../ViewerWrapper';
import CaseIntelligenceHeader from './components/CaseIntelligenceHeader';
import LoadingSkeleton from './components/LoadingSkeleton';
import NoAccessBanner from './components/NoAccessBanner';
import EmptyState from './components/EmptyState';
import CaseIntelligenceDashboard from './components/CaseIntelligenceDashboard';
import CrawlSummaryDrawer from './components/CrawlSummaryDrawer';
import {
  getArtefactCitationSignature,
  getCitationFileId,
  getCitationNavigationLocation,
  getCitationText,
  isCitationNavigationEnabled,
} from './utils/citations';
import 'allotment/dist/style.css';

type CaseIntelligenceRouteUiState = {
  activeTab?: CaseIntelligenceDashboardTabId | null;
  issueFilter?: 'all' | 'legal' | 'theme' | null;
  activeCitationSignature?: string | null;
};

type CaseIntelligenceResolvedUiState = {
  activeTab: CaseIntelligenceDashboardTabId | null;
  issueFilter: 'all' | 'legal' | 'theme';
  activeCitationSignature: string | null;
};

type CaseIntelligenceDashboardTabId = DashboardTabId | 'tags';
const DASHBOARD_TAB_IDS: CaseIntelligenceDashboardTabId[] = [
  'summary',
  'theory',
  'issues',
  'parties',
  'evidence',
  'timeline',
  'action-items',
  'glossary',
  'tags',
];

function getRouteState() {
  const state = (history.location as any)?.state;
  return state && typeof state === 'object' ? (state as Record<string, any>) : {};
}

function getInitialUiState(): CaseIntelligenceResolvedUiState {
  const uiState = getRouteState().caseIntelligenceUiState;
  return {
    activeTab:
      uiState &&
      typeof uiState === 'object' &&
      typeof uiState.activeTab === 'string' &&
      DASHBOARD_TAB_IDS.includes(uiState.activeTab as CaseIntelligenceDashboardTabId)
        ? uiState.activeTab
        : null,
    issueFilter:
      uiState &&
      typeof uiState === 'object' &&
      (uiState.issueFilter === 'all' ||
        uiState.issueFilter === 'legal' ||
        uiState.issueFilter === 'theme')
        ? uiState.issueFilter
        : 'all',
    activeCitationSignature:
      uiState && typeof uiState === 'object' && typeof uiState.activeCitationSignature === 'string'
        ? uiState.activeCitationSignature
        : null,
  };
}

const CaseIntelligencePage: React.FC = () => {
  const initialUiStateRef = useRef(getInitialUiState());
  const dispatch = useDispatch();
  const { caseArtefacts, caseArtefactsPending, caseArtefactsFeedback, fetchCaseArtefacts } =
    useFetchCaseArtefacts();
  const {
    caseTagSuggestions,
    caseTagSuggestionsPending,
    caseTagSuggestionsFeedback,
    fetchCaseTagSuggestions,
  } = useFetchCaseTagSuggestions();
  const { acceptCaseTagSuggestion } = useAcceptCaseTagSuggestion();
  const { rejectCaseTagSuggestion } = useRejectCaseTagSuggestion();
  const { triggerCrawl, triggerCrawlPending } = useTriggerCrawl();
  const { fetchFileDetails } = useFetchFileDetails();
  const currentCase = useSelector(selectCurrentCase);
  const documents = useSelector(selectDocuments) as any[];
  const currentSelectedFile = useSelector(selectCurrentSelectedFileMetaData) as any;
  const routeFileId = useSelector((state: any) => selectSpecificParam(state, 'file')) as
    | string
    | null;
  const dataRoomSettings = useSelector(selectCaseDataRoomSettings);
  const caseId = currentCase?.id || null;
  const claimantName = dataRoomSettings.mergeFilesOptions?.indexPageClaimant || '';
  const respondentName = dataRoomSettings.mergeFilesOptions?.indexPageDefendant || '';

  const [isCrawlSummaryOpen, setIsCrawlSummaryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CaseIntelligenceDashboardTabId | null>(
    initialUiStateRef.current.activeTab,
  );
  const [issueFilter, setIssueFilter] = useState<'all' | 'legal' | 'theme'>(
    initialUiStateRef.current.issueFilter,
  );
  const [activeCitationSignature, setActiveCitationSignature] = useState<string | null>(
    initialUiStateRef.current.activeCitationSignature,
  );
  const [showScrollChrome, setShowScrollChrome] = useState(false);
  const scrollChromeTimeoutRef = useRef<number | null>(null);
  const fileDetailsCacheRef = useRef<Record<string, any>>({});

  useEffect(() => {
    fetchCaseArtefacts().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCaseTagSuggestions().catch(() => {});
  }, [fetchCaseTagSuggestions]);

  const caseArtefactsError = caseArtefactsFeedback?.error?.status ?? null;

  const pageState: PageState = resolvePageState({
    caseArtefacts,
    caseArtefactsPending,
    caseArtefactsError,
  });

  useEffect(() => {
    return () => {
      if (scrollChromeTimeoutRef.current) {
        window.clearTimeout(scrollChromeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const currentState = getRouteState();
    const currentUiState = currentState.caseIntelligenceUiState || {};
    const nextUiState = {
      activeTab,
      issueFilter,
      activeCitationSignature,
    };

    if (
      currentUiState.activeTab === nextUiState.activeTab &&
      currentUiState.issueFilter === nextUiState.issueFilter &&
      currentUiState.activeCitationSignature === nextUiState.activeCitationSignature
    ) {
      return;
    }

    const nextPath = `${history.location.pathname}${history.location.search || ''}${
      history.location.hash || ''
    }`;
    history.replace(nextPath, {
      ...currentState,
      caseIntelligenceUiState: nextUiState,
    });
  }, [activeCitationSignature, activeTab, issueFilter, routeFileId]);

  const handleRegenerate = useCallback(() => {
    triggerCrawl().catch(() => {});
  }, [triggerCrawl]);

  const handleOpenCrawlSummary = useCallback(() => {
    setIsCrawlSummaryOpen(true);
  }, []);

  const handleCloseCrawlSummary = useCallback(() => {
    setIsCrawlSummaryOpen(false);
  }, []);

  const handlePageScroll = useCallback(() => {
    setShowScrollChrome(true);

    if (scrollChromeTimeoutRef.current) {
      window.clearTimeout(scrollChromeTimeoutRef.current);
    }

    scrollChromeTimeoutRef.current = window.setTimeout(() => {
      setShowScrollChrome(false);
      scrollChromeTimeoutRef.current = null;
    }, 900);
  }, []);

  const handleRetryTags = useCallback(() => {
    fetchCaseTagSuggestions().catch(() => {});
  }, [fetchCaseTagSuggestions]);

  const resolveFileDetails = useCallback(
    async (fileId: string) => {
      if (!caseId || !fileId) {
        return null;
      }

      const cacheKey = `${caseId}-${fileId}`;
      const cachedFile = fileDetailsCacheRef.current[cacheKey];
      if (cachedFile) {
        return cachedFile;
      }

      const documentFromList =
        documents?.find((document: any) => {
          const candidates = [
            document?.id,
            document?.fileId,
            document?.folderFileId,
            document?.file?.id,
            document?.file?.fileId,
          ]
            .filter(Boolean)
            .map(String);
          return candidates.includes(String(fileId));
        }) || null;

      if (documentFromList) {
        fileDetailsCacheRef.current[cacheKey] = documentFromList;
        return documentFromList;
      }

      try {
        const result: any = await fetchFileDetails({
          caseId,
          fileId,
          firstTrialBookOnly: true,
        });
        const resolvedFile =
          result?.bundleLocations && result.bundleLocations.length > 0
            ? { ...result.bundleLocations[0], ...result }
            : result;
        fileDetailsCacheRef.current[cacheKey] = resolvedFile;
        return resolvedFile;
      } catch (error) {
        console.error('Failed to fetch case intelligence citation file details', error);
        return null;
      }
    },
    [caseId, documents, fetchFileDetails],
  );

  useEffect(() => {
    if (!routeFileId) {
      return;
    }

    const selectedFileId = String(
      currentSelectedFile?.id ||
        currentSelectedFile?.fileId ||
        currentSelectedFile?.folderFileId ||
        '',
    );

    if (selectedFileId && selectedFileId === String(routeFileId)) {
      return;
    }

    let cancelled = false;

    resolveFileDetails(String(routeFileId)).then((fileDetails) => {
      if (cancelled || !fileDetails) {
        return;
      }

      dispatch(setCurrentSelectedFile(fileDetails));
    });

    return () => {
      cancelled = true;
    };
  }, [currentSelectedFile, dispatch, resolveFileDetails, routeFileId]);

  const handleCitationClick = useCallback(
    async (citation: ArtefactCitation) => {
      if (!isCitationNavigationEnabled(citation)) {
        return;
      }

      const fileId = getCitationFileId(citation);
      if (!fileId) {
        return;
      }

      const nextActiveCitationSignature = getArtefactCitationSignature(citation);
      setActiveCitationSignature(nextActiveCitationSignature);

      const location = getCitationNavigationLocation(citation);
      const docSearchLocation = location
        ? {
            ...location,
            showMarkButton: true,
            timestamp: Date.now(),
          }
        : null;
      const searchText = getCitationText(citation);

      dispatch(setSidebarOpen(false));

      const baseState = getRouteState();
      const nextUiState = {
        activeTab,
        issueFilter,
        activeCitationSignature: nextActiveCitationSignature,
      };
      const nextPath = `${history.location.pathname.replace(/\/files.*/, '')}/files/${fileId}`;
      if (history.location.pathname !== nextPath) {
        history.push(nextPath, {
          ...baseState,
          caseIntelligenceUiState: nextUiState,
        });
      }

      let docSearchDispatched = false;
      const dispatchDocSearchOnce = () => {
        if (docSearchDispatched) {
          return;
        }

        docSearchDispatched = true;
        dispatch((setDocSearch as any)(searchText, docSearchLocation));
      };

      window.setTimeout(dispatchDocSearchOnce, 250);

      const fileDetails = await resolveFileDetails(fileId);
      if (fileDetails) {
        dispatch(setCurrentSelectedFile(fileDetails));
        window.setTimeout(dispatchDocSearchOnce, 50);
      }
    },
    [activeTab, dispatch, issueFilter, resolveFileDetails],
  );

  const viewerFileType =
    currentSelectedFile?.type ||
    currentSelectedFile?.file?.type ||
    currentSelectedFile?.mime ||
    'pdf';

  const primaryContent = (
    <div
      className={`${styles.primaryPane} ${routeFileId ? styles.primaryPaneSplit : ''} ${showScrollChrome ? styles.pageScrolling : ''}`}
      onScroll={handlePageScroll}
      data-testid="case-intelligence-primary-pane"
    >
      <CaseIntelligenceHeader
        triggerCrawlPending={triggerCrawlPending}
        onRegenerate={handleRegenerate}
        onOpenCrawlSummary={handleOpenCrawlSummary}
      />

      {pageState.kind === 'loading' ? (
        <LoadingSkeleton />
      ) : pageState.kind === 'no-access' ? (
        <NoAccessBanner />
      ) : pageState.kind === 'empty' ? (
        <EmptyState onGenerate={handleRegenerate} pending={triggerCrawlPending} />
      ) : pageState.kind === 'ready' ? (
        <CaseIntelligenceDashboard
          artefacts={pageState.artefacts}
          claimantName={claimantName}
          respondentName={respondentName}
          tagSuggestions={caseTagSuggestions}
          tagSuggestionsPending={caseTagSuggestionsPending}
          tagSuggestionsFeedback={caseTagSuggestionsFeedback}
          onRetryTags={handleRetryTags}
          onAcceptTagSuggestion={acceptCaseTagSuggestion}
          onRejectTagSuggestion={rejectCaseTagSuggestion}
          onCitationClick={handleCitationClick}
          activeTab={activeTab}
          onActiveTabChange={(nextTab) => setActiveTab(nextTab)}
          issueFilter={issueFilter}
          onIssueFilterChange={setIssueFilter}
          activeCitationSignature={activeCitationSignature}
        />
      ) : null}
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.pageLayout}>
        {routeFileId ? (
          <Allotment
            defaultSizes={[54, 46]}
            className={styles.pageAllotment}
          >
            <Allotment.Pane minSize={360}>
              <div className={`${styles.splitPaneShell} ${styles.splitPaneShellPrimary}`}>
                {primaryContent}
              </div>
            </Allotment.Pane>
            <Allotment.Pane minSize={420}>
              <div className={`${styles.splitPaneShell} ${styles.splitPaneShellViewer}`}>
                <div className={styles.viewerPane}>
                  <ViewerWrapper
                    fileId={routeFileId}
                    fileType={viewerFileType}
                    isDocFinder={false}
                  />
                </div>
              </div>
            </Allotment.Pane>
          </Allotment>
        ) : (
          primaryContent
        )}
      </div>

      <CrawlSummaryDrawer open={isCrawlSummaryOpen} onClose={handleCloseCrawlSummary} />
    </div>
  );
};

export default CaseIntelligencePage;
